import { useState, useEffect } from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { getRelatedTasksForEmail } from '@/api/todoApi';

interface RelatedTasksProps {
  accountId: string;
  messageId: string;
  limit?: number;
}

interface Task {
  id: string;
  title: string;
  notesMarkdown?: string;
  status: string;
  priority?: string;
  dueDate?: string;
  projectId?: string;
  similarity?: number;
}

export function RelatedTasks({ accountId, messageId, limit = 5 }: RelatedTasksProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!accountId || !messageId) return;

    let cancelled = false;
    setIsLoading(true);

    const runFetch = () => {
      if (cancelled) return;
      getRelatedTasksForEmail(accountId, messageId, limit)
        .then((relatedTasks) => {
          if (!cancelled) {
            const mappedTasks = relatedTasks.map((t) => ({
              id: t.task.id,
              title: t.task.title,
              notesMarkdown: t.task.notesMarkdown,
              status: t.task.status,
              priority: t.task.priority ?? undefined,
              dueDate: t.task.dueDate ?? undefined,
              projectId: t.task.projectId ?? undefined,
              similarity: t.similarity,
            }));
            setTasks(mappedTasks);
            setIsLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setTasks([]);
            setIsLoading(false);
          }
        });
    };

    // P8 (PERF_AUDIT): defer to idle so a thread of M messages doesn't fire M
    // related-tasks lookups during first paint — they run once the conversation
    // is interactive. requestIdleCallback where supported, else a short timeout.
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (typeof w.requestIdleCallback === 'function') {
      idleId = w.requestIdleCallback(runFetch, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(runFetch, 200);
    }

    return () => {
      cancelled = true;
      if (idleId !== null && typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [accountId, messageId, limit]);

  // Hide entirely during loading and when there are no tasks
  if (isLoading || !tasks || tasks.length === 0) {
    return null;
  }

  return (
    <div className="p-4 bg-neutral-surface rounded-lg border border-neutral-line">
      <h3 className="text-sm font-semibold text-neutral-ink mb-3">
        Related Tasks
      </h3>
      <div className="space-y-1">
        {tasks.map((task) => (
          <a
            key={task.id}
            href={`/todos/tasks/${task.id}`}
            className="block p-2 bg-white rounded border border-neutral-line hover:border-info transition-colors"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5">
                {task.status === 'completed' ? (
                  <CheckCircle className="h-4 w-4 text-success-ink" />
                ) : (
                  <Circle className="h-4 w-4 text-neutral-ink" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-ink truncate">
                  {task.title}
                </div>
                {task.notesMarkdown && (
                  <div className="text-xs text-neutral-ink truncate mt-1">
                    {task.notesMarkdown.substring(0, 100)}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {task.similarity !== undefined && (
                    <span className="text-xs text-neutral-ink">
                      {Math.round(task.similarity * 100)}% match
                    </span>
                  )}
                  {task.priority && (
                    <span className="text-xs px-1.5 py-0.5 bg-warning-surface text-warning-ink rounded-full">
                      {task.priority}
                    </span>
                  )}
                  {task.dueDate && (
                    <span className="text-xs text-neutral-ink">
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
